import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import type { OccupancyRow } from "@/libs/analyticsApi";

import { finiteOrNull } from "./finite-field";

/**
 * UtilizationHeatmap — a day × hour grid of court utilization.
 *
 * Pure presentation: it receives an array of per-day rows (each row already an
 * hour-keyed OccupancyRow from getFacilityOccupancy) and renders an accessible
 * grid (role="grid", per-cell role="gridcell" with an aria-label carrying the
 * RAW utilization_percent) coloured on the Business theme's primary ramp.
 *
 * THIN-layer guarantees:
 *   - utilization_percent is ALREADY 0–100. The aria-label and the ramp use it
 *     AS-IS — NEVER multiplied by 100 (that would print "7500%").
 *   - It owns NO fetching and fabricates NO cells: an empty input renders an
 *     honest empty state, not an invented 24-cell grid of zeros.
 *   - Occupancy rows are hour-keyed, so no booking/court UUID is ever rendered.
 *
 * A textual legend anchors the ramp endpoints (0% … 100%) so the colour is
 * interpretable and the state is not conveyed by colour alone.
 */

export interface HeatmapDay {
  /** ISO date (YYYY-MM-DD) for the column/row header. */
  date: string;
  rows: OccupancyRow[];
}

// h:mm-free hour-of-day label, 12h with AM/PM (e.g. "6 PM", "12 PM").
function hourLabel(hour: number): string {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display} ${period}`;
}

// Short day label (e.g. "Jun 8"). Wrap the ISO date in Date first per the
// time rule; "T00:00:00" pins it to local midnight so the label doesn't drift
// a day in negative-offset timezones.
function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T00:00:00`));
}

// Map a raw 0–100 utilization to a primary-tinted background. The percent is
// used AS-IS (already 0–100) — clamp defensively so out-of-range backend data
// can't push the alpha past the [0, 1] the ramp expects. Returns an inline
// background so the rendered intensity varies continuously with the value (the
// test asserts a busy cell is visually distinct from a quiet one).
function intensityColor(util: number, primary: string): string {
  const clamped = Math.min(100, Math.max(0, util));
  // 0% → barely tinted, 100% → full primary. Alpha stays in [0.04, 1].
  const intensity = 0.04 + (clamped / 100) * 0.96;
  return alpha(primary, intensity);
}

export function UtilizationHeatmap({ days }: { days: HeatmapDay[] }) {
  const theme = useTheme();
  const hasData = days.some((day) => day.rows.length > 0);

  if (!hasData) {
    return (
      <Paper
        variant="outlined"
        sx={{ borderStyle: "dashed", px: 2, py: 4, textAlign: "center" }}
      >
        <Typography color="text.secondary" variant="body2">
          No occupancy data for this window.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack aria-label="Court utilization by hour" role="grid" spacing={1}>
        {days.map((day) => (
          <Box
            key={day.date}
            role="row"
            sx={{ alignItems: "center", display: "flex", gap: 1 }}
          >
            <Typography
              color="text.secondary"
              sx={{ flexShrink: 0, width: 64 }}
              variant="caption"
            >
              {dayLabel(day.date)}
            </Typography>
            <Box sx={{ display: "flex", flex: 1, flexWrap: "wrap", gap: 0.5 }}>
              {day.rows.map((row, index) => {
                // The hour + utilization_percent are unenforced (#1340). Gate
                // BOTH to finite numbers: a non-finite utilization renders a
                // blank "no data" cell (NOT a fabricated "0%" or "NaN%"), and a
                // non-finite hour falls back to a neutral hour-less label rather
                // than "NaN PM". utilization_percent is ALREADY 0–100 — AS-IS.
                const util = finiteOrNull(row?.utilization_percent);
                const hour = finiteOrNull(row?.hour);
                const hourText =
                  hour !== null ? hourLabel(hour) : "Unknown hour";
                const key = hour !== null ? `h-${hour}` : `i-${index}`;

                if (util === null) {
                  return (
                    <Box
                      key={key}
                      role="gridcell"
                      aria-label={`${dayLabel(day.date)} ${hourText}: no utilization data`}
                      data-intensity="none"
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        borderStyle: "dashed",
                        color: "text.disabled",
                        display: "grid",
                        flex: "1 1 1.75rem",
                        fontSize: "0.625rem",
                        height: 28,
                        minWidth: 28,
                        placeItems: "center",
                      }}
                      title={`${hourText} — no data`}
                    >
                      &ndash;
                    </Box>
                  );
                }

                const pct = Math.round(util);
                return (
                  <Box
                    key={key}
                    role="gridcell"
                    aria-label={`${dayLabel(day.date)} ${hourText}: ${pct}% utilization (${row.booked_courts} of ${row.total_courts} courts)`}
                    data-intensity={pct}
                    sx={{
                      bgcolor: intensityColor(util, theme.palette.primary.main),
                      borderRadius: 1,
                      color: "text.primary",
                      display: "grid",
                      flex: "1 1 1.75rem",
                      fontSize: "0.625rem",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 700,
                      height: 28,
                      minWidth: 28,
                      placeItems: "center",
                    }}
                    title={`${hourText} — ${pct}%`}
                  >
                    {pct}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Legend — anchors the ramp endpoints so the colour is interpretable.
          The endpoint labels live in a SINGLE caption element ("0% … 100%") so a
          screen reader (and the test) reads one continuous "low to high" key
          rather than two ambiguous standalone "0%" / "100%" nodes. */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography color="text.secondary" variant="caption">
          Utilization
        </Typography>
        <Box
          aria-hidden="true"
          sx={{
            background: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.04,
            )}, ${theme.palette.primary.main})`,
            borderRadius: 999,
            flex: 1,
            height: 8,
          }}
        />
        <Typography
          color="text.secondary"
          sx={{ fontVariantNumeric: "tabular-nums" }}
          variant="caption"
        >
          0% &ndash; 100%
        </Typography>
      </Stack>
    </Stack>
  );
}
